import React, { Component } from "react";
import { api } from "../utils/api";
import toast from "react-hot-toast";
import {
  History,
  RefreshCw,
  Search,
  PlusCircle,
  Edit2,
  Trash2,
  Banknote,
  CheckCircle,
  X,
  Receipt,
  Ban,
} from "lucide-react";

const ACTION_META = {
  create:  { label: "Created",  color: "#0FA968", bg: "#E4F9EE", border: "#BEEED9", Icon: PlusCircle },
  update:  { label: "Updated",  color: "#2F5FE0", bg: "#EFF6FF", border: "#BFDBFE", Icon: Edit2 },
  delete:  { label: "Deleted",  color: "#DC2626", bg: "#FEF2F2", border: "#FECACA", Icon: Trash2 },
  payment: { label: "Payment",  color: "#0B7A4C", bg: "#F0FDF4", border: "#BBF7D0", Icon: Banknote },
  approve: { label: "Approved", color: "#7C3AED", bg: "#F5F3FF", border: "#DDD6FE", Icon: CheckCircle },
  reject:  { label: "Rejected", color: "#B91C1C", bg: "#FEF2F2", border: "#FECACA", Icon: X },
  cancel:  { label: "Cancelled",color: "#B7791F", bg: "#FFFBEB", border: "#FDE68A", Icon: Ban },
  invoice: { label: "Invoiced", color: "#0B7A4C", bg: "#F0FDF4", border: "#BBF7D0", Icon: Receipt },
};

const ENTITY_LABEL = {
  booking: "Booking",
  agent:   "Agent",
  hotel:   "Hotel",
  user:    "User",
  payment: "Payment",
};

function fmtDateTime(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt)) return String(d);
  return dt.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

class ActivityLogPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      logs: [], total: 0, loading: true,
      search: "", actionFilter: "", entityFilter: "",
      from_date: "", to_date: "",
    };
    this.fetch = this.fetch.bind(this);
  }

  componentDidMount() { this.fetch(); }

  async fetch() {
    this.setState({ loading: true });
    try {
      const { search, actionFilter, entityFilter, from_date, to_date } = this.state;
      const params = { limit: 200 };
      if (search) params.search = search;
      if (actionFilter) params.action = actionFilter;
      if (entityFilter) params.entity_type = entityFilter;
      if (from_date) params.from_date = from_date;
      if (to_date) params.to_date = to_date;
      const { data } = await api.get("/activity-logs", { params });
      this.setState({ logs: data.data || [], total: data.total || 0 });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load activity log");
    } finally {
      this.setState({ loading: false });
    }
  }

  render() {
    const { logs, total, loading, search, actionFilter, entityFilter, from_date, to_date } = this.state;

    return (
      <div>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: "#1E2D4F", letterSpacing: "-0.02em" }}>
              Activity <span style={{ color: "#FF6B4A" }}>Log</span>
            </h1>
            <p style={{ fontSize: 13, color: "#6B7280", marginTop: 3 }}>{total} recorded action{total !== 1 ? "s" : ""} — who did what, across bookings, agents, hotels, users, and payments</p>
          </div>
          <button onClick={this.fetch} disabled={loading}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "10px 18px", borderRadius: 10, border: "1.5px solid #D1D5DB", background: "#fff", fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer" }}>
            <RefreshCw size={14} style={loading ? { animation: "spin 1s linear infinite" } : {}} /> Refresh
          </button>
        </div>

        {/* Filters */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 18, background: "#fff", border: "1px solid #E5E7EB", borderRadius: 14, padding: 16 }}>
          <div style={{ position: "relative", flex: "1 1 220px" }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: 11, color: "#9CA3AF" }} />
            <input
              placeholder="Search user, item, or detail..."
              value={search}
              onChange={(e) => this.setState({ search: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && this.fetch()}
              style={{ width: "100%", boxSizing: "border-box", border: "1.5px solid #D1D5DB", borderRadius: 9, padding: "9px 12px 9px 32px", fontSize: 13, outline: "none" }}
            />
          </div>
          <select value={actionFilter} onChange={(e) => this.setState({ actionFilter: e.target.value }, this.fetch)}
            style={{ border: "1.5px solid #D1D5DB", borderRadius: 9, padding: "9px 12px", fontSize: 13, background: "#fff", cursor: "pointer" }}>
            <option value="">All Actions</option>
            {Object.entries(ACTION_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={entityFilter} onChange={(e) => this.setState({ entityFilter: e.target.value }, this.fetch)}
            style={{ border: "1.5px solid #D1D5DB", borderRadius: 9, padding: "9px 12px", fontSize: 13, background: "#fff", cursor: "pointer" }}>
            <option value="">All Types</option>
            {Object.entries(ENTITY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input type="date" value={from_date} onChange={(e) => this.setState({ from_date: e.target.value }, this.fetch)}
            style={{ border: "1.5px solid #D1D5DB", borderRadius: 9, padding: "9px 12px", fontSize: 13 }} />
          <input type="date" value={to_date} onChange={(e) => this.setState({ to_date: e.target.value }, this.fetch)}
            style={{ border: "1.5px solid #D1D5DB", borderRadius: 9, padding: "9px 12px", fontSize: 13 }} />
          <button onClick={this.fetch}
            style={{ padding: "9px 18px", borderRadius: 9, border: "none", background: "#1E2D4F", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            Apply
          </button>
        </div>

        {/* Table */}
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 16, overflow: "hidden" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#9CA3AF" }}>
              <RefreshCw size={24} style={{ animation: "spin 1s linear infinite", marginBottom: 12 }} />
              <p>Loading activity…</p>
            </div>
          ) : logs.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "#9CA3AF" }}>
              <History size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
              <p style={{ fontSize: 15, fontWeight: 600, color: "#374151" }}>No activity found</p>
              <p style={{ fontSize: 13, marginTop: 4 }}>Try adjusting your filters</p>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                <thead>
                  <tr>
                    {["When", "User", "Action", "Type", "Item", "Details", "IP Address"].map((h) => (
                      <th key={h} style={{ padding: "11px 16px", textAlign: "left", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9CA3AF", borderBottom: "1.5px solid #F3F4F6", background: "#FAFBFF", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l, i) => {
                    const meta = ACTION_META[l.action] || { label: l.action, color: "#6B7280", bg: "#F3F4F6", border: "#E5E7EB", Icon: History };
                    const Icon = meta.Icon;
                    return (
                      <tr key={l.id} style={{ background: i % 2 === 0 ? "#fff" : "#FAFBFF" }}>
                        <td style={{ padding: "12px 16px", fontSize: 12, color: "#6B7280", borderBottom: "1px solid #F3F4F6", whiteSpace: "nowrap" }}>{fmtDateTime(l.created_at)}</td>
                        <td style={{ padding: "12px 16px", fontSize: 13, fontWeight: 600, color: "#111827", borderBottom: "1px solid #F3F4F6", whiteSpace: "nowrap" }}>{l.user_name || "System"}</td>
                        <td style={{ padding: "12px 16px", borderBottom: "1px solid #F3F4F6" }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: meta.color, background: meta.bg, border: `1px solid ${meta.border}`, borderRadius: 20, padding: "3px 10px", whiteSpace: "nowrap" }}>
                            <Icon size={11} /> {meta.label}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: 12, color: "#6B7280", borderBottom: "1px solid #F3F4F6", whiteSpace: "nowrap" }}>{ENTITY_LABEL[l.entity_type] || l.entity_type}</td>
                        <td style={{ padding: "12px 16px", fontSize: 13, color: "#111827", borderBottom: "1px solid #F3F4F6" }}>{l.entity_label || "—"}</td>
                        <td style={{ padding: "12px 16px", fontSize: 12, color: "#9CA3AF", borderBottom: "1px solid #F3F4F6", maxWidth: 280 }}>{l.details || "—"}</td>
                        <td style={{ padding: "12px 16px", fontSize: 12, color: "#9CA3AF", borderBottom: "1px solid #F3F4F6", fontFamily: "JetBrains Mono,monospace", whiteSpace: "nowrap" }}>{l.ip_address || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }
}

export default ActivityLogPage;